// Mock data for Cadence Premium extras.
// These are frontend-only fixtures. They reference habits from the seed library
// (h_1..h_12) so labels line up with what the user has picked.

export const PREMIUM_JOBS_HEADING = "Ritual-fit Roles";
export const PREMIUM_JOBS_SUBHEAD =
  "Openings whose shape resembles the habits you already keep. Your rhythm is a résumé.";

export const PREMIUM_JOBS = [
  {
    id: "job_calm",
    title: "Meditation Product Designer",
    company: "Calm Labs",
    location: "Remote · Global",
    salary: "$110k–$140k",
    tags: ["Product design", "Wellness"],
    linked_habits: ["h_1"], // Morning Sit
    why:
      "Your Morning Sit ritual gives you 87 consecutive days of first-hand experience shaping stillness rituals — the exact empathy this team hires for.",
  },
  {
    id: "job_notion",
    title: "Deep-Work Coach (Contract)",
    company: "Notion",
    location: "Hybrid · San Francisco",
    salary: "$95/hr",
    tags: ["Coaching", "Focus"],
    linked_habits: ["h_3"], // Deep Work Block
    why:
      "You keep a 90-minute Deep Work Block four times a week. That's a rare living case study — Notion coaches teach exactly this cadence.",
  },
  {
    id: "job_trailhead",
    title: "Field Researcher, Slow Cities",
    company: "Trailhead Co.",
    location: "Remote · East Coast US",
    salary: "$78k–$92k",
    tags: ["Research", "Outdoors"],
    linked_habits: ["h_5", "h_9"], // Sunset Walk, Long Read
    why:
      "Sunset Walks + Long Reads mark a slow-tempo mind. This role rewards observation, note-taking and unhurried thinking over speed.",
  },
  {
    id: "job_riso",
    title: "Illustration Fellowship",
    company: "Riso Guild",
    location: "Brooklyn, NY · On-site",
    salary: "$3,200/mo stipend",
    tags: ["Craft", "Fellowship"],
    linked_habits: ["h_7"], // Evening Sketch
    why:
      "Your Evening Sketch practice is the exact evidence the Guild looks for — daily craft time, quiet output, no clients yet.",
  },
  {
    id: "job_longform",
    title: "Editorial Assistant",
    company: "LongForm.co",
    location: "Remote · Anywhere",
    salary: "$62k–$70k",
    tags: ["Editorial", "Reading"],
    linked_habits: ["h_9"], // Long Read
    why:
      "40+ minute reading blocks, four days a week. Editorial teams hire for exactly that stamina; most applicants can't prove it.",
  },
  {
    id: "job_stoic",
    title: "Wellness Copywriter",
    company: "Stoic Pods",
    location: "Remote · UK/EU hours",
    salary: "£48k–£58k",
    tags: ["Copy", "Wellness"],
    linked_habits: ["h_2", "h_11"], // Cold Plunge, Breath Ladder
    why:
      "Cold Plunges + Breath Ladders — you write from within the practice, not about it. That voice is unfakeable and this brand is buying it.",
  },
];

// ---------------------------------------------------------------
export const PREMIUM_MEDIA_HEADING = "Curated by Your Circle";
export const PREMIUM_MEDIA_SUBHEAD =
  "Books, films, music and long-reads shortlisted by your matched circle and weighted against the habits you actually keep.";

export const PREMIUM_MEDIA = [
  {
    id: "m_deep_work",
    kind: "Book",
    title: "Deep Work",
    author: "Cal Newport",
    tag: "Focus",
    linked_habits: ["h_3"],
    why: "The literature behind your 90-minute blocks — read once, referred to weekly.",
    circle_votes: 7,
  },
  {
    id: "m_perfect_days",
    kind: "Film",
    title: "Perfect Days",
    author: "Wim Wenders (dir.)",
    tag: "Mindfulness",
    linked_habits: ["h_1", "h_5"],
    why: "A film about the same quiet order you're trying to build. Amara in your circle rewatched it twice this month.",
    circle_votes: 5,
  },
  {
    id: "m_eno",
    kind: "Album",
    title: "Ambient 1: Music for Airports",
    author: "Brian Eno",
    tag: "Deep Work",
    linked_habits: ["h_3"],
    why: "Zero lyrics, gentle envelopes — the circle's #1 background for Deep Work Block.",
    circle_votes: 6,
  },
  {
    id: "m_on_being",
    kind: "Podcast",
    title: "On Being · Krista Tippett",
    author: "APM",
    tag: "Faith",
    linked_habits: ["h_1", "h_10"],
    why: "The interview cadence sits well with a morning routine. Yosef's most-clipped audio this season.",
    circle_votes: 4,
  },
  {
    id: "m_wintering",
    kind: "Book",
    title: "Wintering",
    author: "Katherine May",
    tag: "Rest",
    linked_habits: ["h_12"],
    why: "For your Nightfall Wind-Down. Slow, careful sentences — the circle finished this one in a single week.",
    circle_votes: 5,
  },
  {
    id: "m_ryuichi",
    kind: "Album",
    title: "async",
    author: "Ryuichi Sakamoto",
    tag: "Evening Sketch",
    linked_habits: ["h_7"],
    why: "Kenji recommends this for Evening Sketch — the tempo matches a 30-minute drawing block.",
    circle_votes: 4,
  },
];

// ---------------------------------------------------------------
export const PREMIUM_FRIENDS_HEADING = "Kindred Three";
export const PREMIUM_FRIENDS_SUBHEAD =
  "The three people whose habits and signals most resemble yours. Talk through three habit cycles — then send a friendship request.";

// cycles_shared is the number of habit cycles (a full begin→complete of a shared
// habit) both people have kept while in each other's schedule. Threshold: 3.
export const PREMIUM_FRIENDS = [
  {
    id: "u_2",
    name: "Priya Ramaswamy",
    location: "Bengaluru, IN",
    age: 28,
    avatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=200&h=200&fit=crop",
    overlap_pct: 92,
    signals: ["Same 05:30 wake", "Morning Sit", "Deep Work"],
    cycles_shared: 5,
    tagline: "Reads Cal Newport at bus stops. Never late.",
  },
  {
    id: "u_5",
    name: "Ines Vieira",
    location: "Lisbon, PT",
    age: 31,
    avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200&h=200&fit=crop",
    overlap_pct: 88,
    signals: ["Long Read", "Sunset Walk", "Faith practice"],
    cycles_shared: 2,
    tagline: "Trades louder rooms for a paper book at dusk.",
  },
  {
    id: "u_1",
    name: "Kenji Watanabe",
    location: "Tokyo, JP",
    age: 34,
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop",
    overlap_pct: 85,
    signals: ["Evening Sketch", "Breath Ladder", "Rest ritual"],
    cycles_shared: 1,
    tagline: "Sketches whatever's still. Refills tea instead of scrolling.",
  },
];
